import { useContext } from "react";
import { AppUtils } from "../../../services/AppUtils";
import { ValidationUtils } from "../../../services/ValidationUtils";
import EnrollmentContext from "../../../contexts/EnrollUserContext";
import useEnrollment from "../../../services/useEnrollment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const SwalAlert = withReactContent(Swal);
const NON_DEFAULT_REASON_BODY =
  "Please note, your non default co applicant will not be approved if the relevant document for reason for opting a non default co applicant is not uploaded and hence they will not be eligible for any kind of benefits that are meant for co applicants.";
const RELATIONSHIP_PROOF_BODY =
  "Please note, your co applicant will be approved only if the document for proving the relationship is uploaded and they will be eligible for any kind of benefits that are meant for co applicants only after the approval.";
const UNDERTAKING_REMARKS =
  "Please note that the co applicant will be eligible for the benefits only if the uploaded documents justify your respective selections and get approved. Any further changes once submitted may be chargeable and completely at the discretion of the company.";

const FIELD_DOM_IDS = {
  first_name: "applicantFirstName",
  second_name: "applicantLastName",
  dob: "applicantDob",
  aadhar_no: "applicantAadhar",
  pan_no: "applicantPan",
  gst_no: "applicantGst",
  phone: "contactPhone",
  email: "contactEmail",
  permanent_address_line1: "contactAddress1",
  permanent_address_line2: "contactAddress2",
  permanent_postalcode_id: "permanentPostLocation",
  account_name: "bankAccountName",
  account_no: "bankAccountNo",
  confirm_account_no: "bankConfirmAccountNo",
  ifsc: "bankIfsc",
  coapplicant_name: "caName",
  primary_relationship: "signupPrimaryRelationship",
  coapplicant_relationship_id: "signupOtherRelationship",
  non_default_coapp_reason: "coappNonDefaultReason",
  coapplicant_dob: "caDOB",
  coapplicant_phone: "caMobile",
};

export const useValidatePage = () => {
  const { enrollmentDetails, isPageDirty, setPageError, setFieldErrors, currentPage, setCurrentPage, setEnrollmentDetails, linkID } = useContext(EnrollmentContext);
  const { checkPAN, checkEmail, checkPhone, saveEnrolleeDetails } = useEnrollment(1);

  const isUploadedFile = (value) => typeof File !== "undefined" && value instanceof File;
  const hasDocument = (fileValue, documentName, documentUrl) => Boolean(fileValue || documentName || documentUrl);

  const reportFieldErrors = (fieldErrors) => {
    setFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length === 0) return true;
    setPageError(null);
    const firstKey = Object.keys(FIELD_DOM_IDS).find((key) => fieldErrors[key]);
    if (firstKey) {
      requestAnimationFrame(() => {
        document.getElementById(FIELD_DOM_IDS[firstKey])?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }
    return false;
  };

  const showSkipUploadPopup = async (title, body, question) => {
    const result = await SwalAlert.fire({
      icon: "warning",
      title,
      html: `${body}<br/><br/><strong>${question}</strong>`,
      showCancelButton: true,
      confirmButtonText: "Continue without document",
      cancelButtonText: "Upload document now",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    return result.isConfirmed;
  };

  const showUndertakingPopup = async () => {
    const result = await SwalAlert.fire({
      icon: "info",
      title: "Please note",
      html: `${UNDERTAKING_REMARKS}<br/><br/><strong>Do you agree and want to continue?</strong>`,
      showCancelButton: true,
      confirmButtonText: "Agree and continue",
      cancelButtonText: "Go back and modify",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    return result.isConfirmed;
  };

  return async (navigateToPage = -1) => {
    let pageIsValid = true;
    setPageError(null);
    if (isPageDirty) {
      switch (currentPage) {
        case 0: {
          const basic = enrollmentDetails.basicDetails ?? {};
          const fieldErrors = {};
          if (!String(basic.first_name ?? "").trim()) {
            fieldErrors.first_name = "Please enter your first name";
          }
          if (!String(basic.second_name ?? "").trim()) {
            fieldErrors.second_name = "Please enter your last name";
          }
          const dobText = basic.dob == null ? "" : String(basic.dob).trim();
          if (dobText === "") {
            fieldErrors.dob = "Please enter date of birth";
          } else {
            ValidationUtils.validateDOB(dobText, "Please enter date of birth", (message) => {
              fieldErrors.dob = message;
            });
          }
          const aadhar = String(basic.aadhar_no ?? "").trim();
          if (aadhar && !/^[2-9]\d{11}$/.test(aadhar)) {
            fieldErrors.aadhar_no = "Kindly enter a valid Aadhar Number";
          }
          const pan = String(basic.pan_no ?? "").trim();
          if (pan && !/^[A-Z]{5}\d{4}[A-Z]$/.test(pan)) {
            fieldErrors.pan_no = "Kindly enter a valid PAN Number";
          }
          const gst = String(basic.gst_no ?? "").trim();
          if (gst && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[A-Z0-9]{1}$/.test(gst)) {
            fieldErrors.gst_no = "Kindly enter a valid GST Number";
          }
          if (!reportFieldErrors(fieldErrors)) {
            pageIsValid = false;
            break;
          }
          if ((basic.gst_no === "" && basic.gst_declaration === 0) || (basic.pan_no === "" && basic.pan_declaration === 0)) {
            AppUtils.showDialog("dlgConsent");
            pageIsValid = false;
            break;
          }
          if (pan) {
            let panCheckResult;
            await checkPAN(
              basic.pan_no,
              (data) => {
                if (data?.code === 200) {
                  panCheckResult = true;
                  const nameParts = data?.data?.full_name.split(" ");
                  const firstName = nameParts[0];
                  const lastName = nameParts.slice(1).join(" ");
                  setEnrollmentDetails({
                    ...enrollmentDetails,
                    basicDetails: { ...enrollmentDetails.basicDetails, first_name: firstName, second_name: lastName },
                  });
                }
              },
              (error) => {
                panCheckResult = false;
                reportFieldErrors({
                  pan_no: typeof error === "string" && error ? error : "Kindly enter a valid PAN Number",
                });
              }
            );
            pageIsValid = pageIsValid && panCheckResult;
          }
          break;
        }

        case 1: {
          const contact = enrollmentDetails.contactDetails ?? {};
          const fieldErrors = {};
          const phone = String(contact.phone ?? "").trim();
          if (!/^[6-9]\d{9}$/.test(phone)) {
            fieldErrors.phone = "Please enter a valid phone number";
          }
          const email = String(contact.email ?? "").trim();
          if (!/^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/.test(email)) {
            fieldErrors.email = "Please enter a valid email";
          }
          if (!String(contact.permanent_address_line1 ?? "").trim()) {
            fieldErrors.permanent_address_line1 = "Please enter your permanent address - line 1";
          }
          if (!String(contact.permanent_address_line2 ?? "").trim()) {
            fieldErrors.permanent_address_line2 = "Please enter your permanent address - line 2";
          }
          if (!String(contact.permanent_postalcode_id ?? "").trim()) {
            fieldErrors.permanent_postalcode_id = "Please enter pincode";
          }
          if (!reportFieldErrors(fieldErrors)) {
            pageIsValid = false;
            break;
          }
          const apiErrors = {};
          await checkPhone(
            contact.phone,
            () => {},
            (error) => {
              apiErrors.phone = typeof error === "string" && error ? error : "Please enter a valid phone number";
            }
          );
          await checkEmail(
            contact.email,
            () => {},
            (error) => {
              apiErrors.email = typeof error === "string" && error ? error : "Please enter a valid email";
            }
          );
          if (!reportFieldErrors(apiErrors)) {
            pageIsValid = false;
          }
          break;
        }

        case 2: {
          const bank = enrollmentDetails.bankDetails ?? {};
          const fieldErrors = {};
          if (!String(bank.account_name ?? "").trim()) {
            fieldErrors.account_name = "Please enter your account name";
          }
          if (!String(bank.account_no ?? "").trim()) {
            fieldErrors.account_no = "Please enter your account number";
          }
          if (!String(bank.bank_name ?? "").trim()) {
            fieldErrors.ifsc = "Please select your bank by entering a valid IFSC code";
          }
          if (String(bank.account_no ?? "").trim() && bank.account_no !== bank.confirm_account_no) {
            fieldErrors.confirm_account_no = "Account numbers don't match";
          }
          if (!reportFieldErrors(fieldErrors)) {
            pageIsValid = false;
          }
          break;
        }

        case 3: {
          const coApp = enrollmentDetails.coApplicantDetails ?? {};
          const fieldErrors = {};
          if (!String(coApp.coapplicant_name ?? "").trim()) {
            fieldErrors.coapplicant_name = "Please enter co-applicant name";
          }
          const relationshipId = coApp.coapplicant_relationship_id;
          const relationshipMissing =
            relationshipId === null ||
            relationshipId === undefined ||
            String(relationshipId).trim() === "" ||
            Number.isNaN(relationshipId);
          if (relationshipMissing) {
            if (Number(coApp.is_default_coapplicant) === 0) {
              fieldErrors.coapplicant_relationship_id = "Select co-applicant relationship";
            } else {
              fieldErrors.primary_relationship = "Select co-applicant relationship";
            }
          }
          const dob = String(coApp.coapplicant_dob ?? "").trim();
          if (dob !== "") {
            ValidationUtils.validateDOB(dob, "Please enter co-applicant date of birth", (message) => {
              fieldErrors.coapplicant_dob = message;
            });
          }
          const coAppPhone = String(coApp.coapplicant_phone ?? "").trim();
          if (coAppPhone !== "" && !/^[6-9]\d{9}$/.test(coAppPhone)) {
            fieldErrors.coapplicant_phone = "Enter a valid 10 digit phone number";
          }
          if (Number(coApp.is_default_coapplicant) === 0) {
            const reason = String(coApp.non_default_coapp_reason ?? "").trim();
            if (!reason) {
              fieldErrors.non_default_coapp_reason = "Please enter reason for non-default co-applicant";
            } else if (reason.length > 500) {
              fieldErrors.non_default_coapp_reason = "Reason for non-default co-applicant cannot exceed 500 characters";
            }
          }
          if (!reportFieldErrors(fieldErrors)) {
            pageIsValid = false;
          }

          if (pageIsValid && navigateToPage > currentPage) {
            const hasNonDefaultReasonDocument = hasDocument(
              coApp?.non_default_reason_file,
              coApp?.non_default_coapp_reason_doc_name,
              coApp?.non_default_coapp_reason_doc_url
            );
            const hasRelationshipProofDocument = hasDocument(
              coApp?.coapp_relationship_proof_file,
              coApp?.coapp_relationship_proof_doc_name,
              coApp?.coapp_relationship_proof_url
            );
            const hasUploadedSupportingDocument =
              isUploadedFile(coApp?.non_default_reason_file) || isUploadedFile(coApp?.coapp_relationship_proof_file);

            if (Number(coApp?.is_default_coapplicant) === 0 && !hasNonDefaultReasonDocument) {
              pageIsValid = await showSkipUploadPopup(
                "Upload supporting document?",
                NON_DEFAULT_REASON_BODY,
                "Are you sure that you want to go ahead without uploading this document?"
              );
            }

            if (pageIsValid && !hasRelationshipProofDocument) {
              pageIsValid = await showSkipUploadPopup(
                "Upload relationship document?",
                RELATIONSHIP_PROOF_BODY,
                "Are you sure that you want to go ahead without uploading the relationship document?"
              );
            }

            if (pageIsValid && hasUploadedSupportingDocument) {
              pageIsValid = await showUndertakingPopup();
            }
          }
          break;
        }

        case 4:
          pageIsValid = true;
          break;

        default:
          pageIsValid = true;
          break;
      }
    }

    if (pageIsValid === true && navigateToPage > -1) {
      saveEnrolleeDetails(linkID, currentPage, enrollmentDetails, () => {
        setPageError(null);
        setCurrentPage(navigateToPage);
      });
    }
  };
};
